import { styled } from 'opensolar-ui'
import { useCallback } from 'react'
import { useTranslate } from 'react-admin'
import { FileRecord, ResourcePrefix, UploadStatus, useFileUploadContext } from '../controller/UploadFileContext'
import { formatFileRecordSize, isBlobUrl, isUploadDisabled } from '../utils'
import { UploadFileItemBottomProgress } from './UploadFileItemBottomProgress'
import { UploadFileThumbnail, getPreviewUrl } from './UploadFileThumbnail'

const FileTile = styled('div')(({ theme }) => ({
  position: 'relative',
  width: 215,
  height: 175, // Standard height, but will stretch to match row height
  boxSizing: 'border-box',
  borderRadius: 5,
  overflow: 'hidden',
  border: '3px solid',
  borderColor: theme.palette.grey[300],
  backgroundColor: theme.palette.grey[50],
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    '& .remove-button': {
      opacity: 1,
    },
  },
}))

const FileTileThumbnail = styled('div')({
  flex: 1,
  minHeight: 40,
  display: 'flex',
  overflow: 'hidden',
})

const FileTileFooter = styled('div')(({ theme }) => ({
  padding: 8,
  backgroundColor: 'white',
  borderTop: `1px solid ${theme.palette.grey[300]}`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}))

const FileTileName = styled('div')(({ theme }) => ({
  fontSize: 12,
  fontWeight: 500,
  color: theme.palette.grey[900],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  marginBottom: 2,
}))

const FileTileStatus = styled('div')(({ theme }) => ({
  fontSize: 10,
  color: theme.palette.grey[600],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

const RemoveButton = styled('button')(({ theme }) => ({
  position: 'absolute',
  top: 4,
  right: 4,
  background: 'rgba(0, 0, 0, 0.6)',
  border: 'none',
  borderRadius: '50%',
  width: 24,
  height: 24,
  cursor: 'pointer',
  color: 'white',
  fontSize: 16,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  opacity: 0,
  transition: 'opacity 0.2s',
  '&:hover': {
    background: 'rgba(0, 0, 0, 0.8)',
  },
  '&:disabled': {
    opacity: 0.3,
    cursor: 'not-allowed',
  },
}))

type UploadFileGridProps = {
  slot: string
  resource_prefix: ResourcePrefix
  uploadStatus: UploadStatus
  onFileRemove?: (record: FileRecord) => void
  dropzone?: React.ReactNode
}

const SelectedFilesGrid = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  alignItems: 'stretch', // Make all items in a row have the same height
})

const DropzoneWrapper = styled('div')({
  width: 215, // Match FileTile width
  display: 'flex',
  flexDirection: 'column',
  '& > *': {
    height: '100%', // Make dropzone fill the wrapper height
  },
})

export const UploadFileGrid = ({
  slot,
  resource_prefix,
  uploadStatus,
  onFileRemove,
  dropzone,
}: UploadFileGridProps) => {
  const translate = useTranslate()
  const { fileRecords, setFileRecords } = useFileUploadContext()

  // Get file records for this slot directly from context
  const slotFileRecords = fileRecords.filter((record) => record.slot === slot)

  const removeFile = useCallback(
    (record: FileRecord) => {
      // Remove this record from the context
      const updatedSourceRecords = slotFileRecords.filter((r) => r !== record)
      setFileRecords(slot, updatedSourceRecords, resource_prefix)

      // Clean up object URL if it was created for a pending file
      if (record.status === 'pending' && record.file) {
        const previewUrl = getPreviewUrl(record)
        if (isBlobUrl(previewUrl)) {
          URL.revokeObjectURL(previewUrl)
        }
      }

      // Call optional callback
      onFileRemove?.(record)
    },
    [slot, resource_prefix, slotFileRecords, setFileRecords, onFileRemove]
  )

  const isDisabled = isUploadDisabled(uploadStatus)

  return (
    <SelectedFilesGrid>
      {slotFileRecords.map((record, index) => {
        const statusText = formatFileRecordSize(record, translate)

        return (
          <FileTile key={`${record.status}-${record.name}-${index}`}>
            <FileTileThumbnail>
              <UploadFileThumbnail record={record} />
            </FileTileThumbnail>
            <FileTileFooter>
              <FileTileName title={record.name}>{record.name}</FileTileName>
              {statusText && <FileTileStatus>{statusText}</FileTileStatus>}
            </FileTileFooter>
            <RemoveButton
              className="remove-button"
              disabled={isDisabled}
              type="button"
              onClick={() => removeFile(record)}
              title={translate('Remove file')}
            >
              ×
            </RemoveButton>
            <UploadFileItemBottomProgress fileRecord={record} />
          </FileTile>
        )
      })}
      {dropzone && <DropzoneWrapper>{dropzone}</DropzoneWrapper>}
    </SelectedFilesGrid>
  )
}
